import {
  ActionIcon, AppShell, Avatar, Badge, Box, Burger, Button, Center, Group, Paper, Indicator, Menu, NavLink, Popover, ScrollArea, SegmentedControl, Select,
  Stack, Text, ThemeIcon, Title, Tooltip, UnstyledButton, useComputedColorScheme, useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconApi, IconBell, IconBook2, IconChartBar, IconCheck, IconClipboardCheck, IconHomeEdit, IconInfoCircle, IconAlertTriangle,
  IconCoin, IconFiles, IconLayoutDashboard, IconListDetails, IconLogout, IconMapPin, IconMoon, IconSettings, IconShieldCheck, IconShieldLock,
  IconSearch, IconSun, IconTrees, IconUser,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";

import { api, unwrap, VPN_EVENT } from "./api/client";
import { http } from "./api/http";
import { type CurrentUser, logout } from "./auth/keycloak";
import { useModules } from "./auth/modules";
import { UserContext } from "./auth/user";
import { OpstinaProvider, useOpstina } from "./context/opstina";
import { BrandMarks, BrandTitle } from "./branding";
import { useT } from "./i18n/script";
import AccessRequestsPage from "./pages/admin/AccessRequestsPage";
import GeoservicesPage from "./pages/admin/GeoservicesPage";
import SettingsPage from "./pages/admin/SettingsPage";
import CaseDetailPage from "./pages/cases/CaseDetailPage";
import CasesPage from "./pages/cases/CasesPage";
import MyHoldingPage from "./pages/cases/MyHoldingPage";
import AdminHub from "./pages/admin/AdminHub";
import ImportPage from "./pages/imports/ImportPage";
import AdminUnitsPage from "./pages/admin/AdminUnitsPage";
import AuditPage from "./pages/admin/AuditPage";
import BackupPage from "./pages/admin/BackupPage";
import CodelistsPage from "./pages/admin/CodelistsPage";
import DataRulesPage from "./pages/admin/DataRulesPage";
import DocTypesPage from "./pages/admin/DocTypesPage";
import HtmlTemplatesPage from "./pages/admin/HtmlTemplatesPage";
import PhenoTemplatesPage from "./pages/admin/PhenoTemplatesPage";
import PrivacyPage from "./pages/admin/PrivacyPage";
import RolesPage from "./pages/admin/RolesPage";
import ScriptTermsPage from "./pages/admin/ScriptTermsPage";
import UsersPage from "./pages/admin/UsersPage";
import AnalyticsHub from "./pages/analytics/AnalyticsHub";
import DashboardPage from "./pages/DashboardPage";
import HoldingPage from "./pages/holdings/HoldingPage";
import HoldingsPage from "./pages/holdings/HoldingsPage";
import DmsPage from "./pages/dms/DmsPage";
import FarmPage from "./pages/FarmPage";
import HelpPage from "./pages/HelpPage";
import ProceduresPage from "./pages/procedures/ProceduresPage";
import SubsidiesPage from "./pages/subsidies/SubsidiesPage";
import OrchardDetailPage from "./pages/orchard/OrchardDetailPage";
import UavPage from "./pages/UavPage";

interface NavItem { to: string; label: string; icon: ReactNode; hint: string; perm?: string; module?: string; anyPerm?: string[];
  notPerm?: string }

// Malo stavki: sve o gazdinstvu i voćnjaku (unos, pregled, mapa, 3D, fotografije, uvoz) je u njihovim kartonima.
const NAV: NavItem[] = [
  { to: "/", label: "Početna", hint: "Pregled i statistika", icon: <IconLayoutDashboard size={20} /> },
  { to: "/moje-gazdinstvo", label: "Moje gazdinstvo", hint: "Unos podataka i verifikacija", icon: <IconHomeEdit size={20} />,
    perm: "access:request", notPerm: "cases:read" },
  { to: "/gazdinstva", label: "Gazdinstva i voćnjaci", hint: "Katalog, karton, mapa i 3D", icon: <IconTrees size={20} />,
    anyPerm: ["producers:read", "orchards:read"] },
  { to: "/predmeti", label: "Predmeti", hint: "Registracija i verifikacija gazdinstava", icon: <IconClipboardCheck size={20} />,
    perm: "cases:read" },
  { to: "/postupci", label: "Tehnološki postupci", hint: "Godišnji plan: zaštita, ishrana, navodnjavanje", icon: <IconListDetails size={20} />,
    perm: "diary:read" },
  { to: "/podsticaji", label: "Podsticaji", hint: "Mjere AAP po Pravilniku, provjera uslova, zahtjevi", icon: <IconCoin size={20} />,
    perm: "subsidies:read" },
  { to: "/analitika", label: "Analitika i izvještaji", hint: "Izvještaji, predikcije, evidencije", icon: <IconChartBar size={20} />,
    anyPerm: ["analytics:read", "reports:read", "indices:read"] },
  { to: "/dms", label: "Upravljanje dokumentima", hint: "Djelovodnik, KO i KČ, arhiva", icon: <IconFiles size={20} />, perm: "dms:read" },
  { to: "/admin", label: "Administracija", hint: "Korisnici, šifarnici, uvoz, istorija", icon: <IconSettings size={20} />,
    anyPerm: ["admin:users", "admin:roles", "admin:data-rules", "audit:read", "registry:manage", "system:params", "system:backup",
      "dms:admin", "phenology:templates", "reports:manage", "access:approve", "data:import", "uav:upload", "layers:propose"] },
];

/** Stare adrese (v0.2/v0.3) → nova struktura; parametri se zadržavaju. */
function Redirect({ to }: { to: (p: Record<string, string | undefined>) => string }) {
  const params = useParams();
  const loc = useLocation();
  const target = to(params);
  const sep = target.includes("?") ? "&" : "?";
  return <Navigate to={target + (loc.search ? sep + loc.search.slice(1) : "")} replace />;
}

function Guard({ perm, user, children }: { perm?: string; user: CurrentUser; children: ReactNode }) {
  if (perm && !user.permissions.includes(perm)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function OpstinaSelect() {
  const { t } = useT();
  const { opstina, setOpstina, units } = useOpstina();
  if (units.length <= 1 && !opstina) return null;
  return (
    <Select size="xs" w={{ base: 150, md: 210 }} leftSection={<IconMapPin size={14} />} placeholder={t("Sve opštine")} clearable searchable
      value={opstina} onChange={setOpstina} comboboxProps={{ withinPortal: true }} nothingFoundMessage={t("Nema opštine")}
      data={units.map((u) => ({ value: u.code, label: t(u.name) }))} aria-label={t("Opština")} />
  );
}

/** Uvijek dostupna pretraga u zaglavlju: gazdinstvo (naziv, BPG, nosilac) ili voćnjak → direktno u karton. */
function HoldingSearch() {
  const { t } = useT();
  const nav = useNavigate();
  const [v, setV] = useState<string | null>(null);
  const prod = useQuery({ queryKey: ["producers", "catalog"], staleTime: 120_000,
    queryFn: () => http.get<{ id: number; name: string; bpg: string; holder_name?: string; opstina?: string }[]>("/api/v1/producers", { limit: 2000 }) });
  const orch = useQuery({ queryKey: ["orchards", "catalog"], staleTime: 120_000,
    queryFn: () => http.get<{ id: number; name: string; producer: string | null; bpg: string | null }[]>("/api/v1/orchards") });
  const data = [
    { group: t("Gazdinstva"), items: (prod.data ?? []).map((p) => ({ value: `g${p.id}`, label: `${p.name} · BPG ${p.bpg}${p.holder_name ? ` · ${p.holder_name}` : ""}` })) },
    { group: t("Voćnjaci"), items: (orch.data ?? []).map((o) => ({ value: `v${o.id}`, label: `${o.name} · ${o.producer ?? ""}${o.bpg ? ` (${o.bpg})` : ""}` })) },
  ];
  return (
    <Select size="xs" w={{ base: 160, md: 300 }} searchable clearable value={v} leftSection={<IconSearch size={14} />}
      placeholder={t("Gazdinstvo, BPG ili voćnjak")} data={data} limit={30} nothingFoundMessage={t("Nema rezultata")}
      comboboxProps={{ withinPortal: true, width: 420 }} aria-label={t("Pretraga gazdinstava")}
      onChange={(x) => { setV(null); if (x) nav(x[0] === "g" ? `/gazdinstva/${x.slice(1)}` : `/vocnjaci/${x.slice(1)}`); }} />
  );
}

interface Notif { id: number; title: string; body?: string | null; link?: string | null; kind: string; read_at?: string | null; created_at: string }

const NOTIF_ICON: Record<string, { icon: ReactNode; color: string }> = {
  zadatak: { icon: <IconClipboardCheck size={16} />, color: "agri" }, uspjeh: { icon: <IconCheck size={16} />, color: "green" },
  upozorenje: { icon: <IconAlertTriangle size={16} />, color: "orange" }, info: { icon: <IconInfoCircle size={16} />, color: "blue" },
};

function ago(iso: string): string {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "upravo";
  if (m < 60) return `prije ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `prije ${h} h`;
  return new Date(iso).toLocaleDateString("sr-Latn-BA");
}

/** Obavještenja (dodijeljeni predmeti, odluke, slojevi na odobrenju) — zvonce u zaglavlju. */
function NotificationsBell() {
  const { t } = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["notifications"], refetchInterval: 45_000,
    queryFn: () => http.get<{ items: Notif[]; unread: number }>("/api/v1/notifications", { limit: 20 }) });
  const unread = q.data?.unread ?? 0;
  async function read(n?: Notif) {
    await http.post("/api/v1/notifications/read", undefined, n ? { id: n.id } : undefined);
    qc.invalidateQueries({ queryKey: ["notifications"] });
    if (n?.link) { setOpen(false); nav(n.link); }
  }
  return (
    <Popover opened={open} onChange={setOpen} position="bottom-end" width={360} shadow="lg" radius="md">
      <Popover.Target>
        <Indicator label={unread > 9 ? "9+" : unread} size={16} color="red" disabled={!unread} offset={6}>
          <ActionIcon variant="subtle" color="white" size="lg" onClick={() => setOpen((o) => !o)} aria-label={t("Obavještenja")}>
            <IconBell size={19} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Group justify="space-between" px="sm" py={8} style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
          <Text fw={700} size="sm">{t("Obavještenja")}</Text>
          {unread > 0 && <Button size="compact-xs" variant="subtle" onClick={() => read()}>{t("Označi sve kao pročitano")}</Button>}
        </Group>
        <ScrollArea.Autosize mah={420}>
          {!q.data?.items.length ? <Text size="sm" c="dimmed" p="md" ta="center">{t("Nema obavještenja")}</Text>
            : q.data.items.map((n) => {
              const k = NOTIF_ICON[n.kind] ?? NOTIF_ICON.info;
              return (
                <UnstyledButton key={n.id} w="100%" px="sm" py={8} onClick={() => read(n)}
                  style={{ display: "block", background: n.read_at ? undefined : "var(--mantine-color-agri-light)",
                    borderBottom: "1px solid var(--mantine-color-default-border)" }}>
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <ThemeIcon size={28} radius="xl" variant="light" color={k.color}>{k.icon}</ThemeIcon>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={n.read_at ? 500 : 700} lineClamp={2}>{t(n.title)}</Text>
                      {n.body && <Text size="xs" c="dimmed" lineClamp={2}>{t(n.body)}</Text>}
                      <Text size="xs" c="dimmed" mt={2}>{t(ago(n.created_at))}</Text>
                    </div>
                  </Group>
                </UnstyledButton>
              );
            })}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}

function ThemeToggle() {
  const { setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme("light");
  return (
    <Tooltip label={scheme === "dark" ? "Svijetla tema" : "Tamna tema"}>
      <ActionIcon variant="subtle" color="white" size="lg" onClick={() => setColorScheme(scheme === "dark" ? "light" : "dark")}
        aria-label="Tema">
        {scheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
      </ActionIcon>
    </Tooltip>
  );
}

function Shell({ user }: { user: CurrentUser }) {
  const [opened, { toggle, close }] = useDisclosure();
  const { t, script, setScript } = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const hasModule = useModules();
  const can = (p?: string) => !p || user.permissions.includes(p);
  const items = NAV.filter((n) => can(n.perm) && hasModule(n.module) && (!n.anyPerm || n.anyPerm.some(can))
    && !(n.notPerm && user.permissions.includes(n.notPerm)));
  const active = (to: string) => (to === "/" ? loc.pathname === "/"
    : to === "/gazdinstva" ? /^\/(gazdinstva|vocnjaci)/.test(loc.pathname)
      : to === "/admin" ? /^\/(admin|uvoz|uav|parcele)/.test(loc.pathname)
        : to === "/predmeti" ? loc.pathname.startsWith("/predmeti")
        : loc.pathname === to || loc.pathname.startsWith(to + "/"));

  return (
    <AppShell header={{ height: 56 }} navbar={{ width: 248, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md">
      <AppShell.Header style={{ background: "#1f4d36", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" color="white" />
            <BrandMarks h={38} onDark />
            <Box visibleFrom="sm" maw={360}><BrandTitle extra="AgriSense" /></Box>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {user.permissions.includes("producers:read") && <HoldingSearch />}
            <OpstinaSelect />
            <NotificationsBell />
            <ThemeToggle />
            <SegmentedControl size="xs" value={script} onChange={(v) => setScript(v as "lat" | "cyr")}
              data={[{ value: "lat", label: "Lat" }, { value: "cyr", label: "Ћир" }]} />
            <Menu position="bottom-end" width={280}>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap={8} wrap="nowrap">
                    <Avatar color="agri" variant="filled" radius="xl" size={32}>
                      {(user.name || user.username).slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Text c="white" size="sm" visibleFrom="lg">{user.name || user.username}</Text>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{user.username}</Menu.Label>
                <Group gap={4} px="sm" pb="xs">
                  {user.roles.map((r) => <Badge key={r} size="xs" variant="light">{r}</Badge>)}
                  {(user.assignments ?? []).map((a) => <Badge key={a} size="xs" variant="outline" color="soil">{a}</Badge>)}
                </Group>
                <Menu.Item leftSection={<IconApi size={16} />} component="a" href="/api/docs" target="_blank">Swagger API</Menu.Item>
                <Menu.Item leftSection={<IconShieldCheck size={16} />} component="a" target="_blank"
                  href="/auth/realms/agrisense/account/">{t("Moj nalog i MFA")}</Menu.Item>
                <Menu.Item leftSection={<IconUser size={16} />} onClick={() => nav("/admin/licni-podaci?moji=1")}>{t("Moji podaci (ZZLP)")}</Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={() => logout()}>{t("Odjava")}</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" className="agri-nav">
        <AppShell.Section grow component={ScrollArea}>
          <Stack gap={2} mt={4}>
            {items.map((n) => (
              <NavLink key={n.to} label={t(n.label)} description={<span className="nav-sub">{t(n.hint)}</span>} leftSection={n.icon}
                active={active(n.to)} onClick={() => { nav(n.to); close(); }} py={10} />
            ))}
          </Stack>
        </AppShell.Section>
        <AppShell.Section>
          <NavLink label={t("Uputstvo")} leftSection={<IconBook2 size={18} />} active={active("/uputstvo")}
            onClick={() => { nav("/uputstvo"); close(); }} />
          <Text size="xs" c="#8fb592" px="sm" mt={4}>AgriSense v0.5 · EPSG:8678</Text>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/gazdinstva" element={<HoldingsPage />} />
          <Route path="/gazdinstva/:id" element={<Guard perm="producers:read" user={user}><HoldingPage /></Guard>} />
          <Route path="/vocnjaci/:id" element={<Guard perm="orchards:read" user={user}><OrchardDetailPage /></Guard>} />
          <Route path="/analitika" element={<AnalyticsHub />} />
          <Route path="/dms" element={<Guard perm="dms:read" user={user}><DmsPage /></Guard>} />
          <Route path="/predmeti" element={<Guard perm="cases:read" user={user}><CasesPage /></Guard>} />
          <Route path="/predmeti/:id" element={<CaseDetailPage />} />
          <Route path="/moje-gazdinstvo" element={<Guard perm="access:request" user={user}><MyHoldingPage /></Guard>} />
          <Route path="/uputstvo" element={<HelpPage />} />
          <Route path="/postupci" element={<Guard perm="diary:read" user={user}><ProceduresPage /></Guard>} />
          <Route path="/podsticaji" element={<Guard perm="subsidies:read" user={user}><SubsidiesPage /></Guard>} />
          <Route path="/uvoz" element={<Guard perm="data:import" user={user}><ImportPage /></Guard>} />
          <Route path="/uav" element={<Guard perm="uav:read" user={user}><UavPage /></Guard>} />
          <Route path="/parcele" element={<Guard perm="holdings:read" user={user}><FarmPage /></Guard>} />
          <Route path="/admin" element={<AdminHub />} />
          <Route path="/admin/korisnici" element={<Guard perm="admin:users" user={user}><UsersPage /></Guard>} />
          <Route path="/admin/role" element={<Guard perm="admin:roles" user={user}><RolesPage /></Guard>} />
          <Route path="/admin/pravila" element={<Guard perm="admin:data-rules" user={user}><DataRulesPage /></Guard>} />
          <Route path="/admin/audit" element={<Guard perm="audit:read" user={user}><AuditPage /></Guard>} />
          <Route path="/admin/sifarnici" element={<Guard perm="registry:manage" user={user}><CodelistsPage /></Guard>} />
          <Route path="/admin/opstine" element={<Guard perm="registry:manage" user={user}><AdminUnitsPage /></Guard>} />
          <Route path="/admin/fenologija" element={<Guard perm="phenology:read" user={user}><PhenoTemplatesPage /></Guard>} />
          <Route path="/admin/dms" element={<Guard perm="dms:read" user={user}><DocTypesPage /></Guard>} />
          <Route path="/admin/pismo" element={<Guard perm="registry:manage" user={user}><ScriptTermsPage /></Guard>} />
          <Route path="/admin/html-sabloni" element={<Guard perm="reports:manage" user={user}><HtmlTemplatesPage /></Guard>} />
          <Route path="/admin/podesavanja" element={<Guard perm="system:params" user={user}><SettingsPage /></Guard>} />
          <Route path="/admin/parametri" element={<Redirect to={() => "/admin/podesavanja"} />} />
          <Route path="/admin/slojevi" element={<Guard perm="layers:propose" user={user}><GeoservicesPage /></Guard>} />
          <Route path="/admin/backup" element={<Guard perm="system:backup" user={user}><BackupPage /></Guard>} />
          <Route path="/admin/zahtjevi-pristup" element={<Guard perm="access:approve" user={user}><AccessRequestsPage /></Guard>} />
          <Route path="/admin/licni-podaci" element={<PrivacyPage />} />
          {/* stare adrese */}
          <Route path="/proizvodjaci" element={<Redirect to={() => "/gazdinstva"} />} />
          <Route path="/proizvodjaci/:id" element={<Redirect to={(p) => `/gazdinstva/${p.id}`} />} />
          <Route path="/vocarstvo" element={<Redirect to={() => "/gazdinstva?pogled=vocnjaci"} />} />
          <Route path="/vocarstvo/:id" element={<Redirect to={(p) => `/vocnjaci/${p.id}`} />} />
          <Route path="/mapa" element={<Redirect to={() => "/gazdinstva?pogled=mapa"} />} />
          <Route path="/pristup" element={<Redirect to={() => "/moje-gazdinstvo"} />} />
          <Route path="/statistika" element={<Redirect to={() => "/analitika?tab=statistika"} />} />
          <Route path="/fenologija" element={<Redirect to={() => "/analitika?tab=fenologija"} />} />
          <Route path="/knjiga" element={<Redirect to={() => "/analitika?tab=knjiga"} />} />
          <Route path="/izvjestaji" element={<Redirect to={() => "/analitika?tab=izvjestaji"} />} />
          <Route path="/indeksi" element={<Redirect to={() => "/analitika?tab=indeksi"} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}

function VpnRequired({ message }: { message: string }) {
  const { t } = useT();
  return (
    <Center h="100vh" p="md">
      <Paper withBorder radius="lg" p="xl" maw={520} shadow="md">
        <Stack align="center" gap="sm">
          <ThemeIcon size={56} radius="xl" color="orange" variant="light"><IconShieldLock size={30} /></ThemeIcon>
          <Title order={3} ta="center">{t("Potreban je VPN Agencije")}</Title>
          <Text ta="center" size="sm">{t(message)}</Text>
          <Text ta="center" size="xs" c="dimmed">{t("WireGuard aplikacija → uključite tunel „AgriSense“. Ako nemate VPN profil, obratite se informatičkoj službi Agencije.")}</Text>
          <Group>
            <Button onClick={() => window.location.reload()}>{t("Pokušaj ponovo")}</Button>
            <Button variant="default" leftSection={<IconLogout size={16} />} onClick={() => logout()}>{t("Odjava")}</Button>
          </Group>
        </Stack>
      </Paper>
    </Center>
  );
}

export default function App() {
  const { t } = useT();
  const [vpn, setVpn] = useState<string | null>(null);
  useEffect(() => {
    const h = (e: Event) => setVpn((e as CustomEvent<string>).detail || "VPN");
    window.addEventListener(VPN_EVENT, h);
    return () => window.removeEventListener(VPN_EVENT, h);
  }, []);
  const me = useQuery({ queryKey: ["me"], queryFn: async () => unwrap(await api.GET("/api/v1/auth/me")) });
  if (vpn) return <VpnRequired message={vpn} />;
  if (me.isLoading) return <Text p="xl">{t("Učitavanje...")}</Text>;
  if (me.error || !me.data) return <Text p="xl" c="red">{t("Greška pri učitavanju korisnika")}: {String(me.error)}</Text>;
  const user = me.data as CurrentUser;
  return (
    <UserContext.Provider value={user}>
      <OpstinaProvider>
        <Shell user={user} />
      </OpstinaProvider>
    </UserContext.Provider>
  );
}
